import * as React from 'react';

type SpecialistSiteId = 'stickers' | 'reclameborden' | 'posters' | 'canvas' | 'mapwall';

interface SpecialistBrandMarkProps {
  siteId: string;
  dark?: boolean;
  compact?: boolean;
}

const accessibleNames: Record<SpecialistSiteId, string> = {
  stickers: 'bestelstickers.nl',
  reclameborden: 'bestelreclamebord.nl',
  posters: 'bestelposters.nl',
  canvas: 'bestelfotocanvas.nl',
  mapwall: 'mapwall.eu',
};

const wordmarkFont = 'var(--font-inter), Inter, Arial, sans-serif';

function StickerWordmark({ dark }: { dark: boolean }) {
  const ink = dark ? '#ffffff' : '#071936';
  const lime = dark ? '#b9ea2d' : '#92c60e';
  return (
    <>
      <text x="3" y="43" fill={ink} fontFamily={wordmarkFont} fontSize="39" fontWeight="790" letterSpacing="-1.9">bestel</text>
      <path d="M119 7h24.5c5.2 0 9.5 4.3 9.5 9.5V48h-28.5c-5.2 0-9.5-4.3-9.5-9.5v-22c0-5.2 4-9.5 4-9.5Z" fill="none" stroke={lime} strokeWidth="2.6" />
      <path d="M115 38.5c0 5.2 4.3 9.5 9.5 9.5h9.7c-6.4 0-8.9-4.3-8.9-10.7Z" fill={dark ? '#6fa6ff' : '#155dfc'} />
      <text x="118" y="43" fill={lime} fontFamily={wordmarkFont} fontSize="39" fontWeight="790" letterSpacing="-2">stickers</text>
      <circle cx="274.5" cy="40" r="3.1" fill={ink} />
      <text x="281" y="43" fill={ink} fontFamily={wordmarkFont} fontSize="21" fontWeight="790" letterSpacing="-1">nl</text>
    </>
  );
}

function BoardWordmark({ dark }: { dark: boolean }) {
  const ink = dark ? '#ffffff' : '#071936';
  const blue = dark ? '#74a0ff' : '#155dfc';
  const amber = '#f2a900';
  return (
    <>
      <rect x="2.5" y="2.5" width="281" height="59" rx="10.5" fill="none" stroke={blue} strokeWidth="2.5" />
      <circle cx="12" cy="12" r="3" fill={amber} />
      <circle cx="274" cy="12" r="3" fill={amber} />
      <text x="19" y="19" fill={ink} fontFamily={wordmarkFont} fontSize="15" fontWeight="720" letterSpacing="-.7">bestel</text>
      <path d="M19 25h245" stroke={amber} strokeWidth="2.3" />
      <text x="18" y="52" fill={blue} fontFamily={wordmarkFont} fontSize="31" fontWeight="800" letterSpacing="-1.55">reclamebord</text>
      <circle cx="244" cy="49.5" r="2.7" fill={ink} />
      <text x="250" y="52" fill={ink} fontFamily={wordmarkFont} fontSize="18" fontWeight="790" letterSpacing="-.8">nl</text>
    </>
  );
}

function PosterWordmark({ dark }: { dark: boolean }) {
  const ink = dark ? '#ffffff' : '#071936';
  const coral = dark ? '#ff7668' : '#ee4b3e';
  const blue = dark ? '#78a4ff' : '#155dfc';
  return (
    <>
      <text x="3" y="43" fill={ink} fontFamily={wordmarkFont} fontSize="39" fontWeight="790" letterSpacing="-1.9">bestel</text>
      <path d="M117 7h18v18h-18Z" fill={coral} />
      <path d="m117 7 18 18V7Z" fill={blue} />
      <text x="116" y="43" fill={coral} fontFamily={wordmarkFont} fontSize="39" fontWeight="790" letterSpacing="-2">posters</text>
      <circle cx="259" cy="40" r="3" fill={ink} />
      <text x="266" y="43" fill={ink} fontFamily={wordmarkFont} fontSize="21" fontWeight="790" letterSpacing="-1">nl</text>
    </>
  );
}

function CanvasWordmark({ dark }: { dark: boolean }) {
  const ink = dark ? '#fffaf5' : '#071936';
  const sage = dark ? '#a8c9b3' : '#779c86';
  const clay = dark ? '#e3a07e' : '#bd684b';
  return (
    <>
      <text x="4" y="18" fill={ink} fontFamily={wordmarkFont} fontSize="15.5" fontWeight="730" letterSpacing="-.65">bestel</text>
      <text x="52" y="18" fill={sage} fontFamily={wordmarkFont} fontSize="15.5" fontWeight="730" letterSpacing="-.65">foto</text>
      <rect x="89" y="4" width="30" height="19" rx="2" fill="none" stroke={clay} strokeWidth="2.2" />
      <path d="M93 19h22v-3.5l-5.5-5-5 4-3.8-3.4Z" fill={sage} />
      <text x="3" y="54" fill={sage} fontFamily={wordmarkFont} fontSize="42" fontWeight="780" letterSpacing="-2.2">canvas</text>
      <circle cx="143" cy="51" r="3" fill={clay} />
      <text x="150" y="54" fill={ink} fontFamily={wordmarkFont} fontSize="22" fontWeight="780" letterSpacing="-1">nl</text>
    </>
  );
}

function MapWordmark({ dark }: { dark: boolean }) {
  const ink = dark ? '#ffffff' : '#071936';
  return (
    <>
      <path d="M22 49S40 35 40 19a18 18 0 1 0-36 0c0 16 18 30 18 30Z" fill={ink} />
      <circle cx="22" cy="19" r="7" fill="#f2a900" />
      <text x="50" y="42" fill={ink} fontFamily={wordmarkFont} fontSize="36" fontWeight="790" letterSpacing="-2">mapwall</text>
      <circle cx="188" cy="39" r="3" fill="#f2a900" />
      <text x="195" y="42" fill={ink} fontFamily={wordmarkFont} fontSize="20" fontWeight="790">eu</text>
    </>
  );
}

const logoGeometry: Record<SpecialistSiteId, { viewBox: string; widthClass: string; compactHeightClass?: string }> = {
  stickers: { viewBox: '0 0 312 60', widthClass: 'max-w-[178px] sm:max-w-[208px]' },
  reclameborden: { viewBox: '0 0 286 64', widthClass: 'max-w-[176px] sm:max-w-[205px]' },
  posters: { viewBox: '0 0 294 60', widthClass: 'max-w-[166px] sm:max-w-[196px]' },
  canvas: { viewBox: '0 0 184 60', widthClass: 'max-w-[142px] sm:max-w-[166px]', compactHeightClass: 'h-10 sm:h-11' },
  mapwall: { viewBox: '0 0 226 54', widthClass: 'max-w-[166px] sm:max-w-[194px]' },
};

export function SpecialistBrandMark({ siteId, dark = false, compact = false }: SpecialistBrandMarkProps) {
  const normalizedId = siteId in accessibleNames ? siteId as SpecialistSiteId : 'stickers';
  const geometry = logoGeometry[normalizedId];

  return (
    <svg
      viewBox={geometry.viewBox}
      className={`${compact ? geometry.compactHeightClass || 'h-9 sm:h-10' : 'h-11 sm:h-12'} w-auto ${geometry.widthClass}`}
      role="img"
      aria-label={accessibleNames[normalizedId]}
      preserveAspectRatio="xMinYMid meet"
    >
      {normalizedId === 'stickers' && <StickerWordmark dark={dark} />}
      {normalizedId === 'reclameborden' && <BoardWordmark dark={dark} />}
      {normalizedId === 'posters' && <PosterWordmark dark={dark} />}
      {normalizedId === 'canvas' && <CanvasWordmark dark={dark} />}
      {normalizedId === 'mapwall' && <MapWordmark dark={dark} />}
    </svg>
  );
}
